import * as Yup from 'yup';
import { useMemo } from 'react';
import { enqueueSnackbar } from 'notistack';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';

import { DatePicker } from '@mui/x-date-pickers';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  Typography,
} from '@mui/material';

import { fCurrencyRupees } from 'src/utils/format-number';

import { useAppDispatcher } from 'src/redux/store';
import { createIssuedChequePayment } from 'src/redux/slices/issued-cheque';

import FormProvider, { RHFTextField } from 'src/components/hook-form';

import { IIssuedChequeItem } from 'src/types/issued-cheque';

type Props = {
  cheque: IIssuedChequeItem;
  isOpen: boolean;
  onClose: VoidFunction;
  onSuccess: VoidFunction;
};

export default function IssuedChequePaymentDialog({ cheque, isOpen, onClose, onSuccess }: Props) {
  const dispatch = useAppDispatcher();

  const remainingInCents = cheque.amountInCents - cheque.paidInCents;

  const schema = useMemo(
    () =>
      Yup.object().shape({
        paymentDate: Yup.date().required('Payment date is required'),
        amount: Yup.number()
          .typeError('Amount must be a number')
          .moreThan(0, 'Amount must be more than 0')
          .max(
            remainingInCents / 100,
            `Cannot be more than the remaining ${fCurrencyRupees(remainingInCents)}`
          )
          .required('Amount is required'),
        note: Yup.string().max(500, 'Note must be at most 500 characters'),
      }),
    [remainingInCents]
  );

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: { paymentDate: new Date(), amount: remainingInCents / 100, note: '' },
    mode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      await dispatch(
        createIssuedChequePayment({
          id: cheque._id,
          paymentDate: new Date(data.paymentDate),
          amountInCents: Math.round(data.amount * 100),
          note: data.note || undefined,
        })
      ).unwrap();

      enqueueSnackbar('Payment recorded');
      reset();
      onSuccess();
      onClose();
    } catch (e) {
      enqueueSnackbar(typeof e === 'string' ? e : e?.message || 'Failed to record the payment', {
        variant: 'error',
      });
    }
  });

  return (
    <Dialog fullWidth maxWidth="xs" open={isOpen} onClose={onClose}>
      <DialogTitle sx={{ pb: 2 }}>Add Payment</DialogTitle>

      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogContent sx={{ typography: 'body2' }}>
          <Typography sx={{ mb: 2 }} variant="body2">
            Money paid to <strong>{cheque.payee}</strong> in place of this cheque. Remaining:{' '}
            <strong>{fCurrencyRupees(remainingInCents)}</strong>
          </Typography>

          <Stack spacing={2}>
            <Controller
              name="paymentDate"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DatePicker
                  label="Payment Date"
                  disableFuture
                  value={field.value}
                  onChange={(newValue) => field.onChange(newValue)}
                  slotProps={{
                    textField: { fullWidth: true, error: !!error, helperText: error?.message },
                  }}
                />
              )}
            />

            <RHFTextField
              required
              name="amount"
              label="Amount"
              type="number"
              InputProps={{
                startAdornment: <InputAdornment position="start">Rs</InputAdornment>,
              }}
            />

            <RHFTextField
              name="note"
              label="Note (optional)"
              multiline
              rows={2}
              placeholder="e.g. Bank transfer, cash"
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={onClose}>
            Cancel
          </Button>

          <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
            Save Payment
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
